import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

// ── Types ─────────────────────────────────────────────────────────────

interface Step {
  n: number;
  title: string;
  detail: string;
}

interface Flag {
  name: string;
  value: string;
  desc: string;
}

interface Module {
  id: string;
  title: string;
  status: 'live' | 'send-gated';
  flag: string | null;
  demoNote: string | null;
  whatItDoes: string;
  whatItReplaces: string;
  steps: Step[];
  flags: Flag[];
  audienceNote: string | null;
  validationNote: string | null;
}

interface FeedbackItem {
  feedback_id: string;
  module: string;
  reviewer: string;
  notes: string;
  timestamp: string;
  session_id: string;
}

// ── Module definitions (authoritative — do not modify) ────────────────

const MODULES: Module[] = [
  {
    id: "ai-claim-summary",
    title: "AI Claim Summary",
    status: "live",
    flag: null,
    demoNote: "Best demo claim: TCM-00070-0000059 (Yardstik) — complex fact pattern, strong AI narrative.",
    whatItDoes: "Reads all available claim data from FileHandler and generates a plain-English summary: parties, timeline, reserves, diary highlights, and risk flags — in seconds.",
    whatItReplaces: "Manually reading through a FileHandler claim screen and mentally assembling context before a call or coverage decision. No more screen-scrolling.",
    steps: [
      { n: 1, title: "Open Sidecar portal → Claims", detail: "Navigate to sidecar.canopymsp.com and click Claims in the sidebar." },
      { n: 2, title: "Search for a claim number", detail: "Enter a TCM claim number. Sidecar fetches the claim from FileHandler in real time." },
      { n: 3, title: "Review the AI summary", detail: "The panel shows: claimant, coverage type, reserves breakdown (loss vs LAE), key dates, open diary items, and any flags for litigation, coverage disputes, or deductible triggers." },
      { n: 4, title: "Use the summary — don't replace judgment", detail: "The AI surfaces context. Handlers still own decisions. Think of it as a fast briefing before a call." },
    ],
    flags: [],
    audienceNote: null,
    validationNote: null,
  },
  {
    id: "loss-run-generator",
    title: "Loss Run Generator",
    status: "live",
    flag: "72 coverholders",
    demoNote: null,
    whatItDoes: "Queries FileHandler directly and generates a formatted Word document loss run for any coverholder — open claims, reserves, payments, Net to Client — grouped by contract/UMR.",
    whatItReplaces: "Dorella's manual process: pulling from FileHandler, formatting in Word, calculating net figures. Currently takes hours per report across 72+ coverholders.",
    steps: [
      { n: 1, title: "Navigate to Loss Runs", detail: "sidecar.canopymsp.com → Loss Runs" },
      { n: 2, title: "Select an organization", detail: "Choose from the dropdown. Good demo orgs: Veracity Insurance Solutions or Convex." },
      { n: 3, title: "Set the cutoff date and hit Generate", detail: "Sidecar queries the FileHandler Report DB, applies open-status filters, calculates Net to Client figures, and builds the Word document." },
      { n: 4, title: "Download the Word doc", detail: "Formatted and ready. Shows all open claims grouped by contract, with reserves, payments, deductibles, and Net to Client column." },
    ],
    flags: [],
    audienceNote: 'Shelle framing: "This replaces what Dorella produces manually — same format, same data, but it takes 15 seconds instead of a few hours per report."',
    validationNote: "Validation items still open before showing to Dorella or broader T11 staff: open status filter logic, Net to Client deductible calc, 72 vs. 73 coverholder discrepancy. Use for demo only until Richard confirms.",
  },
  {
    id: "blue-sheets",
    title: "Blue Sheet Workflow",
    status: "live",
    flag: "FH_API_ENABLED=false",
    demoNote: null,
    whatItDoes: "Replaces paper Blue Sheets with a digital change request workflow. Handlers submit changes, managers approve with a named sign-off, and every action is logged with timestamp.",
    whatItReplaces: "Lucas manually cross-referencing ~40 paper Blue Sheets per day. Physical signatures with no audit trail. Approval bottlenecks causing processing delays.",
    steps: [
      { n: 1, title: "Navigate to Blue Sheets", detail: "sidecar.canopymsp.com → Blue Sheets (Change Requests)" },
      { n: 2, title: "Click New Request (handler)", detail: "Enter the claim number, field being changed, old value, new value, and reason." },
      { n: 3, title: "Submit for approval", detail: "Request routes to Kathy Weber or the designated approver. Status shows as Pending." },
      { n: 4, title: "Approve or reject (manager)", detail: "Your name and timestamp are recorded automatically. No additional signature required." },
    ],
    flags: [
      { name: "FH_API_ENABLED", value: "false", desc: "Write-back to FileHandler is stubbed. Approvals are logged in Sidecar but must still be entered in FileHandler manually. Will flip when JW Software provides write API access (est. 6–12 months)." }
    ],
    audienceNote: 'Shelle framing: "Same process — Lucas submits, Kathy or Becky approves — but now every authorization is named, timestamped, and auditable. Controls are stronger than the paper process."',
    validationNote: null,
  },
  {
    id: "bdx-generator",
    title: "BDX Coverholder Generator",
    status: "send-gated",
    flag: "BDX_SEND_ENABLED=false",
    demoNote: null,
    whatItDoes: "Auto-generates cumulative Excel bordereaux workbooks and TCM fee invoice PDFs for each coverholder program every month. Reports queue for approval before anything is sent.",
    whatItReplaces: "Becky's manual monthly workflow: building Excel workbooks per coverholder, calculating TCM fees from invoice records, emailing to London brokers (CBC UK, Price Forbes).",
    steps: [
      { n: 1, title: "Navigate to BDX", detail: "sidecar.canopymsp.com → BDX. The queue shows all auto-generated reports for the current month." },
      { n: 2, title: "Open a report (e.g. Concord Health – Feb 2026)", detail: "See claim count, contract year tabs, and TCM fees pulled directly from FileHandler invoice records." },
      { n: 3, title: "Download the workbook and invoice PDF", detail: "Workbook: 6 contract year tabs, all claims, TCM fees ($67,280 cumulative for Concord). Invoice PDF: line-item breakdown per contract year." },
      { n: 4, title: "Spot-check against last month", detail: "Compare to what Becky sent to CBC UK last month. If values match, approval pathway opens." },
      { n: 5, title: "Approve to queue for send", detail: "Once Shelle confirms output is correct, BDX_SEND_ENABLED flips to true and the email goes to the broker contact on file." },
    ],
    flags: [
      { name: "BDX_SEND_ENABLED", value: "false", desc: "No email leaves the system until Shelle validates output and this flag is flipped. Reports queue indefinitely at the approval step." }
    ],
    audienceNote: 'Shelle framing: "This automates what Becky does every month — the bordereaux and invoice that goes to CBC UK. Same output, zero manual work."',
    validationNote: null,
  },
  {
    id: "convex-automation",
    title: "Convex Loss Run Automation",
    status: "send-gated",
    flag: "CONVEX_SEND_ENABLED=false",
    demoNote: null,
    whatItDoes: "Monitors T11's shared mailbox for incoming loss run requests. AI classifies each email, extracts the claim reference, looks it up in FileHandler, and prepares a response document for approval.",
    whatItReplaces: "Manual email triage: reading incoming requests, identifying the insurer, finding the claim in FileHandler, generating and sending a response. Currently entirely manual.",
    steps: [
      { n: 1, title: "Email arrives in the shared mailbox", detail: "ConvexUSnewclaimreporting@trans11claims.com is polled every 5 minutes via Microsoft Graph API." },
      { n: 2, title: "AI classifies the email", detail: "Bedrock Claude reads subject and body, identifies the insurer, extracts the claim reference number, and routes accordingly." },
      { n: 3, title: "Triage categories", detail: "Category A: manual. Category B: needs intervention. Category C: ready to generate — fully automated." },
      { n: 4, title: "Human reviews and approves in the portal", detail: "Navigate to Convex Loss Runs. Review the queued item. Click Approve. Nothing leaves without approval." },
    ],
    flags: [
      { name: "CONVEX_SEND_ENABLED", value: "false", desc: "Hardcoded off. No email leaves the system under any condition without explicit human approval in the portal." }
    ],
    audienceNote: null,
    validationNote: null,
  },
  {
    id: "sla-reporting",
    title: "SLA Reporting",
    status: "live",
    flag: "33 metrics · 6 categories",
    demoNote: null,
    whatItDoes: "Queries FileHandler, evaluates performance against configured thresholds, and produces formatted SLA reports (Format A and B) per organization on a monthly schedule.",
    whatItReplaces: "Kim's manual SLA report process — pulling data from FileHandler, calculating metrics against thresholds, formatting in Excel, distributing to program contacts.",
    steps: [
      { n: 1, title: "Navigate to SLA Reporting", detail: "sidecar.canopymsp.com → SLA Reporting. The report queue shows all scheduled and queued reports." },
      { n: 2, title: "Open a queued report", detail: "See the organization, reporting period, and metric results. Metrics flagged amber or red are at or near threshold breaches." },
      { n: 3, title: "Review metric results", detail: "33 metrics across 6 categories. Metrics marked N/A are pending data validation with Kim." },
      { n: 4, title: "Approve and send", detail: "Approve the report. Generates a downloadable Excel file (Format A or B per org config) and queues for email delivery." },
      { n: 5, title: "Configure org settings", detail: "Each org has its own threshold overrides, metric selection, format, and delivery contacts." },
    ],
    flags: [],
    audienceNote: null,
    validationNote: "15 metrics are currently marked N/A — pending Kim's confirmation of correct claim_date/claim_code field mappings. Not a blocker for demo or walkthroughs.",
  },
];

const REVIEWERS = ['Richard', 'Noah', 'Joshua', 'Shelle'];

// ── Session ID (persists in localStorage) ─────────────────────────────

function getSessionId(): string {
  const key = 'guide_session_id';
  let sessionId = localStorage.getItem(key);
  if (!sessionId) {
    const rand = Math.random().toString(36).substring(2, 6);
    const date = new Date().toISOString().slice(0, 10);
    sessionId = `session-${date}-${rand}`;
    localStorage.setItem(key, sessionId);
  }
  return sessionId;
}

// ── Feedback Log View ─────────────────────────────────────────────────

function FeedbackLog() {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getAllGuideFeedback();
      setItems(data.items || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const exportCsv = () => {
    const headers = ['Module', 'Reviewer', 'Notes', 'Timestamp', 'Session ID'];
    const rows = items.map(i => [
      i.module,
      i.reviewer,
      `"${(i.notes || '').replace(/"/g, '""')}"`,
      i.timestamp,
      i.session_id,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `guide-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const moduleTitle = (id: string) => MODULES.find(m => m.id === id)?.title || id;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-800">Feedback Log</h2>
        <button
          onClick={exportCsv}
          disabled={items.length === 0}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
        >
          Export CSV
        </button>
      </div>

      {loading && <p className="text-slate-500 text-sm">Loading feedback...</p>}
      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">{error}</p>}

      {!loading && !error && items.length === 0 && (
        <p className="text-slate-500 text-sm">No feedback submitted yet.</p>
      )}

      {!loading && items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className="py-2 px-3 font-semibold text-slate-600">Module</th>
                <th className="py-2 px-3 font-semibold text-slate-600">Reviewer</th>
                <th className="py-2 px-3 font-semibold text-slate-600">Notes</th>
                <th className="py-2 px-3 font-semibold text-slate-600">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.feedback_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-3 font-medium text-slate-700">{moduleTitle(item.module)}</td>
                  <td className="py-2 px-3 text-slate-600">{item.reviewer}</td>
                  <td className="py-2 px-3 text-slate-600 max-w-md truncate" title={item.notes}>
                    {item.notes.length > 80 ? item.notes.slice(0, 80) + '...' : item.notes}
                  </td>
                  <td className="py-2 px-3 text-slate-500 whitespace-nowrap">
                    {new Date(item.timestamp).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Module Content Panel ──────────────────────────────────────────────

function ModulePanel({ mod }: { mod: Module }) {
  const [selectedReviewers, setSelectedReviewers] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [existingFeedback, setExistingFeedback] = useState<FeedbackItem[]>([]);

  useEffect(() => {
    api.getGuideFeedback(mod.id)
      .then(data => setExistingFeedback(data.items || []))
      .catch(() => {});
  }, [mod.id]);

  const handleSave = async () => {
    if (selectedReviewers.length === 0 || !notes.trim()) return;
    setSaving(true);
    setFeedbackError('');
    setSaved(false);
    try {
      const sessionId = getSessionId();
      for (const reviewer of selectedReviewers) {
        await api.saveGuideFeedback({
          module: mod.id,
          reviewer,
          notes: notes.trim(),
          session_id: sessionId,
        });
      }
      setSaved(true);
      setNotes('');
      // Refresh feedback list
      const data = await api.getGuideFeedback(mod.id);
      setExistingFeedback(data.items || []);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setFeedbackError(e.message || 'Failed to save feedback');
    } finally {
      setSaving(false);
    }
  };

  const toggleReviewer = (name: string) => {
    setSelectedReviewers(prev =>
      prev.includes(name) ? prev.filter(r => r !== name) : [...prev, name]
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-2xl font-bold text-slate-800">{mod.title}</h2>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
            mod.status === 'live'
              ? 'bg-green-100 text-green-700'
              : 'bg-amber-100 text-amber-700'
          }`}>
            {mod.status === 'live' ? 'Live' : 'Send-Gated'}
          </span>
          {mod.flag && (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
              {mod.flag}
            </span>
          )}
        </div>
      </div>

      {/* Demo note */}
      {mod.demoNote && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
          <span className="font-semibold">Demo tip:</span> {mod.demoNote}
        </div>
      )}

      {/* What it does / replaces */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <h3 className="font-semibold text-slate-700 mb-2">What it does</h3>
          <p className="text-sm text-slate-600">{mod.whatItDoes}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <h3 className="font-semibold text-slate-700 mb-2">What it replaces</h3>
          <p className="text-sm text-slate-600">{mod.whatItReplaces}</p>
        </div>
      </div>

      {/* Steps */}
      <div>
        <h3 className="font-semibold text-slate-700 mb-3">Step-by-step</h3>
        <ol className="space-y-3">
          {mod.steps.map(step => (
            <li key={step.n} className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">
                {step.n}
              </span>
              <div>
                <div className="font-medium text-slate-800 text-sm">{step.title}</div>
                <div className="text-sm text-slate-500 mt-0.5">{step.detail}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Feature flags */}
      {mod.flags.length > 0 && (
        <div>
          <h3 className="font-semibold text-slate-700 mb-2">Feature flags</h3>
          {mod.flags.map(f => (
            <div key={f.name} className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-2">
              <div className="flex items-center gap-2 mb-1">
                <code className="text-xs font-mono bg-amber-100 px-1.5 py-0.5 rounded">{f.name}={f.value}</code>
              </div>
              <p className="text-sm text-amber-800">{f.desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* Audience note */}
      {mod.audienceNote && (
        <div className="bg-purple-50 border border-purple-200 rounded-md p-3 text-sm text-purple-800">
          <span className="font-semibold">Framing:</span> {mod.audienceNote}
        </div>
      )}

      {/* Validation note */}
      {mod.validationNote && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
          <span className="font-semibold">Validation:</span> {mod.validationNote}
        </div>
      )}

      {/* Feedback section */}
      <div className="border-t border-slate-200 pt-6">
        <h3 className="font-semibold text-slate-700 mb-3">Feedback</h3>

        {/* Reviewer pills */}
        <div className="flex flex-wrap gap-2 mb-3">
          {REVIEWERS.map(name => (
            <button
              key={name}
              onClick={() => toggleReviewer(name)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selectedReviewers.includes(name)
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {name}
            </button>
          ))}
        </div>

        {/* Notes textarea */}
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          placeholder="Notes from walkthrough..."
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
        />

        <div className="flex items-center gap-3 mt-2">
          <button
            onClick={handleSave}
            disabled={saving || selectedReviewers.length === 0 || !notes.trim()}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Save Feedback'}
          </button>
          {saved && <span className="text-green-600 text-sm font-medium">Saved</span>}
          {feedbackError && <span className="text-red-600 text-sm">{feedbackError}</span>}
        </div>

        {/* Existing feedback for this module */}
        {existingFeedback.length > 0 && (
          <div className="mt-4 space-y-2">
            <h4 className="text-sm font-medium text-slate-500">Previous feedback</h4>
            {existingFeedback.map(fb => (
              <div key={fb.feedback_id} className="bg-slate-50 rounded-md p-3 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-slate-700">{fb.reviewer}</span>
                  <span className="text-slate-400 text-xs">
                    {new Date(fb.timestamp).toLocaleString()}
                  </span>
                </div>
                <p className="text-slate-600">{fb.notes}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Guide Page ───────────────────────────────────────────────────

export default function Guide() {
  const [activeTab, setActiveTab] = useState<string>(MODULES[0].id);
  const [showFeedbackLog, setShowFeedbackLog] = useState(false);

  const activeModule = MODULES.find(m => m.id === activeTab);

  return (
    <div className="flex h-full">
      {/* Module sidebar */}
      <div className="w-56 flex-shrink-0 border-r border-slate-200 bg-white p-4">
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
          Modules
        </h3>
        <nav className="space-y-1">
          {MODULES.map(mod => (
            <button
              key={mod.id}
              onClick={() => { setActiveTab(mod.id); setShowFeedbackLog(false); }}
              className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === mod.id && !showFeedbackLog
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
              }`}
            >
              <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                mod.status === 'live' ? 'bg-green-500' : 'bg-amber-500'
              }`} />
              {mod.title}
            </button>
          ))}
        </nav>

        <div className="border-t border-slate-200 mt-4 pt-4">
          <button
            onClick={() => setShowFeedbackLog(true)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              showFeedbackLog
                ? 'bg-blue-50 text-blue-700'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
            }`}
          >
            <span className="mr-2">&#128203;</span>
            Feedback Log
          </button>
        </div>
      </div>

      {/* Content panel */}
      <div className="flex-1 overflow-auto p-6 bg-gray-50">
        {showFeedbackLog ? (
          <FeedbackLog />
        ) : activeModule ? (
          <ModulePanel mod={activeModule} />
        ) : null}
      </div>
    </div>
  );
}
